import * as React from 'react'
import { motion } from 'framer-motion'
import { cn } from '../../utils/cn'

/**
 * JPG Academy Native Select
 *
 * A styled wrapper around the native <select> element for cases where
 * the Radix-based Select is too heavy (e.g., inline spreadsheet cells).
 *
 * Usage:
 *   <NativeSelect
 *     label="Status"
 *     options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]}
 *   />
 *   <NativeSelect options={opts} inputSize="sm" />
 */

export interface NativeSelectOption {
	value: string
	label: string
}

export interface NativeSelectProps
	extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
	label?: string
	options: NativeSelectOption[]
	inputSize?: 'sm' | 'md'
	/** Error message */
	error?: string
	/** Hint text */
	hint?: string
	className?: string
}

const sizeStyles = {
	sm: 'h-8 text-xs px-2 pr-7',
	md: 'h-10 text-sm px-3 pr-8',
}

export const NativeSelect = React.forwardRef<HTMLSelectElement, NativeSelectProps>(
	(
		{
			className,
			label,
			options,
			inputSize = 'md',
			error,
			hint,
			id,
			...props
		},
		ref,
	) => {
		const selectId = id || label?.toLowerCase().replace(/\s+/g, '-')

		return (
			<motion.div
				className="space-y-1.5"
				initial={{ opacity: 0, y: 4 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
			>
				{label && (
					<label
						htmlFor={selectId}
						className="block text-sm font-medium text-foreground font-ui"
					>
						{label}
					</label>
				)}
				<div className="relative">
					<select
						ref={ref}
						id={selectId}
						className={cn(
							'w-full font-ui rounded-md border appearance-none',
							'transition-all duration-fast ease-default',
							'bg-background text-foreground',
							'focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold',
							'disabled:cursor-not-allowed disabled:opacity-50',
							sizeStyles[inputSize],
							error
								? 'border-error/60 focus:ring-error/30 focus:border-error'
								: 'border-input hover:border-navy-300',
							className,
						)}
						aria-invalid={!!error}
						aria-describedby={
							error
								? `${selectId}-error`
								: hint
									? `${selectId}-hint`
									: undefined
						}
						{...props}
					>
						{options.map((opt) => (
							<option key={opt.value} value={opt.value}>
								{opt.label}
							</option>
						))}
					</select>
					{/* Chevron icon */}
					<svg
						className={cn(
							'absolute top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground',
							inputSize === 'sm'
								? 'w-3 h-3 right-2'
								: 'w-4 h-4 right-2.5',
						)}
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<path d="m6 9 6 6 6-6" />
					</svg>
				</div>
				{error && (
					<motion.p
						id={`${selectId}-error`}
						className="text-xs text-error font-ui"
						initial={{ opacity: 0, y: -4, height: 0 }}
						animate={{ opacity: 1, y: 0, height: 'auto' }}
						exit={{ opacity: 0, y: -4, height: 0 }}
						transition={{ duration: 0.2 }}
					>
						{error}
					</motion.p>
				)}
				{hint && !error && (
					<p
						id={`${selectId}-hint`}
						className="text-xs text-muted-foreground font-ui"
					>
						{hint}
					</p>
				)}
			</motion.div>
		)
	},
)

NativeSelect.displayName = 'NativeSelect'
